import React, { useCallback, useEffect, useRef, useState } from 'react';
import { contentInventoryService, InventoryEntry, InventoryStatus, PendingCheck } from '../../../../services/contentInventoryService';
import { DataTable, DataTableColumn } from '../../shared/DataTable';
import { ContentInventoryDetail } from './ContentInventoryDetail';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EntryRow = InventoryEntry & { key: string };

// ---------------------------------------------------------------------------
// List Columns
// ---------------------------------------------------------------------------

const COLUMNS: DataTableColumn<EntryRow>[] = [
  {
    key: 'title',
    header: 'Title',
    render: (row) => (
      <>
        <div className="font-medium text-gray-900">{row.title}</div>
        <div className="text-xs text-gray-400 mt-0.5 truncate max-w-md">{row.url}</div>
      </>
    ),
  },
  {
    key: 'author',
    header: 'Author',
    render: (row) => <span className="text-gray-500">{row.author || '—'}</span>,
  },
  {
    key: 'category',
    header: 'Category',
    render: (row) => <span className="text-gray-500">{row.category || '—'}</span>,
  },
  {
    key: 'pillar',
    header: 'Pillar',
    render: (row) => (
      <span className="text-gray-500">{row.enrichment?.content_pillar || '—'}</span>
    ),
  },
  {
    key: 'keywords',
    header: 'Keywords',
    render: (row) => {
      const kws = row.enrichment?.keywords || [];
      if (!kws.length) return <span className="text-gray-300">—</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {kws.slice(0, 3).map((kw) => (
            <span key={kw} className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">{kw}</span>
          ))}
          {kws.length > 3 && <span className="text-xs text-gray-400">+{kws.length - 3}</span>}
        </div>
      );
    },
  },
  {
    key: 'words',
    header: 'Words',
    className: 'w-20',
    render: (row) => (
      <span className="text-gray-500 font-mono text-xs">{row.word_count?.toLocaleString() || '—'}</span>
    ),
  },
  {
    key: 'type',
    header: 'Type',
    className: 'w-16',
    render: (row) => (
      <span className="text-xs text-gray-400 uppercase">{row.content_type}</span>
    ),
  },
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const ContentInventorySection: React.FC = () => {
  const [entries, setEntries] = useState<EntryRow[]>([]);
  const [status, setStatus] = useState<InventoryStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('post');
  const [selectedEntry, setSelectedEntry] = useState<InventoryEntry | null>(null);
  const [pending, setPending] = useState<PendingCheck | null>(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingExpanded, setPendingExpanded] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const resp = await contentInventoryService.pendingCheck();
      setPending(resp);
    } catch {
      // Silent — pending check is non-critical
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listResp, statusResp] = await Promise.all([
        contentInventoryService.list(contentTypeFilter),
        contentInventoryService.status(),
      ]);
      setEntries((listResp.entries || []).map((e) => ({ ...e, key: e.url })));
      setStatus(statusResp);
      if (statusResp.sync?.status === 'running') {
        const deadline = statusResp.sync.deadline ? new Date(statusResp.sync.deadline).getTime() : 0;
        const updatedAt = statusResp.sync.updated_at ? new Date(statusResp.sync.updated_at).getTime() : 0;
        const isStale = deadline ? Date.now() > deadline : Date.now() - updatedAt > 20 * 60 * 1000;
        if (!isStale) {
          setSyncing(true);
          startPolling();
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [contentTypeFilter]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const statusResp = await contentInventoryService.status();
        setStatus(statusResp);
        const deadline = statusResp.sync?.deadline ? new Date(statusResp.sync.deadline).getTime() : 0;
        const pastDeadline = deadline && Date.now() > deadline;
        if (statusResp.sync?.status !== 'running' || pastDeadline) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          setSyncing(false);
          const listResp = await contentInventoryService.list(contentTypeFilter);
          setEntries((listResp.entries || []).map((e) => ({ ...e, key: e.url })));
        }
      } catch {
        // Ignore poll errors
      }
    }, 5000);
  }, [contentTypeFilter]);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Check for pending updates on mount and when sync completes
  useEffect(() => {
    if (!syncing) {
      void checkPending();
    }
  }, [syncing, checkPending]);

  const handleSync = useCallback(async (fullRebuild: boolean) => {
    setSyncing(true);
    setError(null);
    try {
      await contentInventoryService.sync(fullRebuild);
      startPolling();
    } catch (e: any) {
      setError(e?.message || 'Sync failed');
      setSyncing(false);
    }
  }, [startPolling]);

  // --- Detail view ---
  if (selectedEntry) {
    return <ContentInventoryDetail
      entry={selectedEntry}
      onBack={() => { setSelectedEntry(null); void loadData(); }}
      onEntryUpdated={(_url, enrichment) => {
        setSelectedEntry((prev) => prev ? { ...prev, enrichment } : prev);
      }}
    />;
  }

  // --- List view ---
  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Content Inventory</h1>
          {status && (
            <p className="text-sm text-gray-500 mt-1">
              {status.posts} posts · {status.pages} pages · {status.enriched} enriched
              {status.last_synced && (
                <> · Last synced: {new Date(status.last_synced).toLocaleDateString()}</>
              )}
            </p>
          )}
          {!status && !loading && (
            <p className="text-sm text-gray-500 mt-1">
              No inventory data yet. Click "Sync from WordPress" to build it.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSync(false)}
            disabled={syncing}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncing ? 'Syncing...' : 'Sync from WordPress'}
          </button>
          <button
            onClick={() => handleSync(true)}
            disabled={syncing}
            className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Re-fetch all posts and re-enrich everything"
          >
            Full rebuild
          </button>
        </div>
      </div>

      {/* Pending updates banner */}
      {!syncing && pending && pending.pending_count > 0 && (
        <div className="mb-4 px-4 py-3 rounded-md bg-amber-50 border border-amber-200 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span>
                <strong>{pending.pending_count}</strong> article{pending.pending_count !== 1 ? 's' : ''} new or modified in WordPress since last sync
              </span>
            </div>
            <button
              type="button"
              className="text-xs text-amber-700 hover:text-amber-900 font-medium underline"
              onClick={() => setPendingExpanded(!pendingExpanded)}
            >
              {pendingExpanded ? 'Hide' : 'View'}
            </button>
          </div>
          {pendingExpanded && (
            <ul className="mt-3 space-y-1.5 text-sm text-amber-900 max-h-64 overflow-y-auto">
              {pending.pending_articles.map((a) => (
                <li key={a.wp_id} className="flex items-start gap-2">
                  <span className="text-xs uppercase font-mono text-amber-600 mt-0.5">{a.content_type}</span>
                  <a href={a.link} target="_blank" rel="noopener noreferrer" className="hover:underline flex-1" dangerouslySetInnerHTML={{ __html: a.title }} />
                  <span className="text-xs text-amber-600 shrink-0">{a.modified ? new Date(a.modified).toLocaleDateString() : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {!syncing && pending && pending.pending_count === 0 && pending.high_water_mark && (
        <div className="mb-4 px-4 py-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800 flex items-center gap-2">
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Inventory is up to date with WordPress</span>
        </div>
      )}

      {/* Sync status banner */}
      {syncing && (
        <div className="mb-4 px-4 py-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-800 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse shrink-0" />
          <span>{status?.sync?.step || 'Syncing from WordPress...'}</span>
        </div>
      )}
      {!syncing && status?.sync?.status === 'completed' && (
        <div className="mb-4 px-4 py-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800">
          Sync complete: {status.sync.new ?? 0} new, {status.sync.modified ?? 0} modified, {status.sync.enriched ?? 0} enriched. Total: {status.sync.total ?? status.total} entries.
        </div>
      )}
      {!syncing && status?.sync?.status === 'failed' && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          Sync failed: {status.sync.error || 'Unknown error'}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center bg-gray-100 rounded-md p-0.5 text-xs">
          {(['post', 'page', 'all'] as const).map((type) => (
            <button
              key={type}
              type="button"
              className={`px-3 py-1 rounded transition-all ${
                contentTypeFilter === type
                  ? 'bg-white shadow-sm text-gray-900 font-medium'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setContentTypeFilter(type)}
            >
              {type === 'all' ? 'All' : type === 'post' ? 'Posts' : 'Pages'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
        </div>
      ) : (
        <DataTable
          columns={COLUMNS}
          rows={entries}
          onRowClick={(row) => setSelectedEntry(row)}
          emptyMessage="No entries. Click 'Sync from WordPress' to populate the inventory."
        />
      )}
    </div>
  );
};
